import React, { useState, useEffect } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import PlusMinQty from "../../utilities/PlusMinQty";
import { Info, X } from "lucide-react";

const defSLTP = ["Comb Prem", "PNL"];

const StrategyRiskModal = ({ show, setShow, popUpData, info, onSave }) => {
  const [formData, setFormData] = useState({});
  const [errorMessages, setErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [calcVal, setcalcVal] = useState(0);
  const [slValueType, setSlValueType] = useState(2);
  const [tpValueType, setTpValueType] = useState(2);
  const [trailValueType, setTrailValueType] = useState(2);
  const [perLot, setPerLot] = useState(false);
  const [showScaleWarning, setShowScaleWarning] = useState(false);

  useEffect(() => {
    if (popUpData) {
      const strategyRms = popUpData?.strategyRms || {};
      const normalizedFormData = {
        ...popUpData,
        strategyRms: {
          basis: strategyRms.basis ?? 0,
          takeProfitInput: strategyRms.takeProfitInput || null,
          stopLossInput: strategyRms.stopLossInput || null,
          trailSL: strategyRms.trailSL || null,
          profitLockAt: strategyRms.profitLockAt || null,
          profitLockSl: strategyRms.profitLockSl || null,
          profitLockTrail: strategyRms.profitLockTrail || null,
        },
        sltpType: popUpData?.sltpType || (strategyRms.basis === 2 ? "Underlying" : (strategyRms.basis === 1 ? "Comb Prem" : "PNL")),
      };
      setFormData(normalizedFormData);
      const currentSltpType = normalizedFormData.sltpType || (strategyRms.basis === 1 ? "Comb Prem" : "PNL");
      const slType = strategyRms?.stopLossInput?.valueType;
      const tpType = strategyRms?.takeProfitInput?.valueType;
      if (currentSltpType === "PNL") {
        setSlValueType(slType ?? 0);
        setTpValueType(tpType ?? 0);
      } else {
        setSlValueType((slType === 0 || !slType) ? 2 : (slType === 1 || slType === 2) ? slType : 2);
        setTpValueType((tpType === 0 || !tpType) ? 2 : (tpType === 1 || tpType === 2) ? tpType : 2);
      }
      setTrailValueType(strategyRms?.trailSL?.valueType ?? 2);
      setPerLot(!!strategyRms.perLot);
    }
  }, [popUpData]);

  useEffect(() => {
    if (popUpData && popUpData.row && popUpData.row.data) {
      const data = popUpData.row.data;
      let buyPremium = 0;
      let sellPremium = 0;
      for (let i = 0; i < data.length; i++) {
        const leg = data[i] || {};
        const netQty = parseFloat(leg.netQuantity || 0);
        const qty = Math.abs(netQty || parseFloat(leg.quantity || 0) || 0);
        const lotSize = parseFloat(leg.instrument?.lotSize || leg.lotSize || 1);
        const lots = lotSize > 0 ? (qty / lotSize) : 0;
        const ltp = parseFloat(leg.ltp || 0);
        const sideInput = leg.transactionType || leg.side || (netQty < 0 ? "SELL" : netQty > 0 ? "BUY" : null);
        const side = sideInput ? String(sideInput).toUpperCase() : null;

        if (side) {
          if (perLot) {
            if (side === "BUY") buyPremium += ltp;
            else if (side === "SELL") sellPremium += ltp;
          } else if (lots > 0) {
            if (side === "BUY") buyPremium += ltp * lots;
            else if (side === "SELL") sellPremium += ltp * lots;
          }
        }
      }
      setcalcVal(buyPremium - sellPremium);
    }
  }, [popUpData, perLot]);

  const handleReset = () => {
    if (popUpData) {
      const strategyRms = popUpData?.strategyRms || {};
      const resetFormData = {
        ...popUpData,
        strategyRms: {
          basis: strategyRms.basis ?? 0,
          takeProfitInput: null,
          stopLossInput: null,
          trailSL: null,
          profitLockAt: null,
          profitLockSl: null,
          profitLockTrail: null,
        },
        sltpType: popUpData?.sltpType || (strategyRms.basis === 2 ? "Underlying" : (strategyRms.basis === 1 ? "Comb Prem" : "PNL")),
      };
      setFormData(resetFormData);
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      const currentSltpType = resetFormData.sltpType || (strategyRms.basis === 1 ? "Comb Prem" : "PNL");
      if (currentSltpType === "PNL") {
        setSlValueType(0);
        setTpValueType(0);
      } else {
        setSlValueType(2);
        setTpValueType(2);
      }
      setTrailValueType(2);
    }
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const onClickSlType = () => {
    const basis = formData?.sltpType;
    const newSlValueType = basis === "PNL" ?
      cycleWithin(slValueType, [0, 2]) :
      cycleWithin(slValueType, [1, 2]);

    setSlValueType(newSlValueType);
    isValidsltp("sl", formData?.strategyRms?.stopLossInput?.value, newSlValueType);
  };

  const onClickTpType = () => {
    const basis = formData?.sltpType;
    const newTpValueType = basis === "PNL" ?
      cycleWithin(tpValueType, [0, 2]) :
      cycleWithin(tpValueType, [1, 2]);

    setTpValueType(newTpValueType);
    isValidsltp("tp", formData?.strategyRms?.takeProfitInput?.value, newTpValueType);
  };
  const onClickTrailType = () => {
    setTrailValueType(prev => cycleWithin(prev, [2, 1]));
  };

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  const setMsg = (type, ok, customMsg = null) => {
    if (type === "sl") {
      const msg = customMsg
      setErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : msg }));
    } else {
      const msg = customMsg
      setErrorMessages((prev) => ({ ...prev, target: ok ? "" : msg }));
    }
  };

  const isValidsltp = (type, val, overrideType = null) => {
    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(type, true);
      return true;
    }
    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(type, true);
      return true;
    }

    const basisIsPNL = formData.sltpType === "PNL";
    if (!basisIsPNL) {
      if (parsedVal <= 0) {
        setMsg(type, false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        return false;
      }
    } else {
      const valueType = overrideType ?? (type === "sl" ? slValueType : tpValueType);
      if (type === "sl" && parsedVal < 0 && valueType == 2) {
        setMsg(type, false, "Stop Loss cannot be negative");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && valueType == 2) {
        setMsg(type, false, "Target cannot be negative");
        return false;
      }
      if (type === "sl" && parsedVal > 0 && valueType !== 2) {
        setMsg(type, false, "Stop Loss cannot be positive");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && valueType !== 2) {
        setMsg(type, false, "Target cannot be negative");
        return false;
      }
    }
    setMsg(type, true);
    return true;
  };

  useEffect(() => {
    if (!show || !popUpData || !formData || Object.keys(formData).length === 0) return;

    const stopLossValue = formData?.strategyRms?.stopLossInput?.value;
    if (stopLossValue !== null && stopLossValue !== undefined && stopLossValue !== '' && stopLossValue !== 0) {
      isValidsltp("sl", stopLossValue);
    }

    const targetValue = formData?.strategyRms?.takeProfitInput?.value;
    if (targetValue !== null && targetValue !== undefined && targetValue !== '' && targetValue !== 0) {
      isValidsltp("tp", targetValue);
    }

    validateTrailSL(formData?.strategyRms?.trailSL?.reference, formData?.strategyRms?.trailSL?.step);

    validateProfitLock(formData?.strategyRms?.profitLockAt, formData?.strategyRms?.profitLockSl);

    validateProfitLockTrail(formData?.strategyRms?.profitLockTrail?.reference, formData?.strategyRms?.profitLockTrail?.step);
  }, []);

  const handlePerLotChange = (e) => {
    const checked = e.target.checked;
    setPerLot(checked);
    if (checked && (formData.stopLossAbsolute || formData.takeProfitAbsolute)) {
      setShowScaleWarning(true);
    } else if (!checked) {
      setShowScaleWarning(false);
    }
  };

  useEffect(() => {
    if (!formData.stopLossAbsolute && !formData.takeProfitAbsolute) {
      setShowScaleWarning(false);
    }
  }, [formData.stopLossAbsolute, formData.takeProfitAbsolute]);

  const handleFieldChange = (val, name, headersValue) => {
    if (name !== "sltpType") {
      const parsed = (!val && val !== 0) ? null : parseFloat(val);
      const updated = { ...formData, strategyRms: { ...(formData.strategyRms || {}) } };

      if (name === "stopLossAbsolute") {
        if (parsed === null || isNaN(parsed)) {
          updated.strategyRms.stopLossInput = null;
          updated.strategyRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, stopLoss: "", trailSL: "" }));
        } else {
          isValidsltp("sl", parsed);
          updated.strategyRms.stopLossInput = {
            valueType: formData?.strategyRms?.stopLossInput?.valueType ?? slValueType,
            value: parsed
          };
        }
      } else if (name === "takeProfitAbsolute") {
        if (parsed === null || isNaN(parsed)) {
          updated.strategyRms.takeProfitInput = null;
          setErrorMessages((prev) => ({ ...prev, target: "" }));
        } else {
          isValidsltp("tp", parsed);
          updated.strategyRms.takeProfitInput = {
            valueType: formData?.strategyRms?.takeProfitInput?.valueType ?? tpValueType,
            value: parsed
          };
        }
      } else if (name === "trailReferenceAbsolute" || name === "trailStepAbsolute") {
        const base = { ...(formData?.strategyRms?.trailSL || {}) };
        if (name === "trailReferenceAbsolute") base.reference = parsed;
        if (name === "trailStepAbsolute") base.step = parsed;
        if (base.reference == null && base.step == null) {
          updated.strategyRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          updated.strategyRms.trailSL = {
            valueType: formData?.strategyRms?.trailSL?.valueType ?? trailValueType,
            reference: base.reference ?? null,
            step: base.step ?? null
          };
          // Validate after updating the structure (don't return early - allow state update)
          validateTrailSL(updated.strategyRms.trailSL.reference, updated.strategyRms.trailSL.step);
        }
      } else if (name === "profitLockAt" || name === "profitLockSl") {
        updated.strategyRms[name] = parsed;
        if (parsed === null) {
          updated.strategyRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        }
        const newAt = name === "profitLockAt" ? updated.strategyRms.profitLockAt : formData.strategyRms?.profitLockAt;
        const newSl = name === "profitLockSl" ? updated.strategyRms.profitLockSl : formData.strategyRms?.profitLockSl;
        validateProfitLock(newAt, newSl);
      } else if (name === "profitLockTrailRef" || name === "profitLockTrailStep") {
        const base = { ...(formData?.strategyRms?.profitLockTrail || {}) };
        if (name === "profitLockTrailRef") base.reference = parsed;
        if (name === "profitLockTrailStep") base.step = parsed;
        if (base.reference == null && base.step == null) {
          updated.strategyRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          updated.strategyRms.profitLockTrail = {
            valueType: 2,
            reference: base.reference ?? null,
            step: base.step ?? null
          };
          validateProfitLockTrail(base.reference, base.step);
        }
      }
      setFormData(updated);
    } else {
      const updated = { ...formData, sltpType: val };
      if (val === "PNL") {
        setSlValueType(0);
        setTpValueType(0);
      } else {
        setSlValueType(2);
        setTpValueType(2);
      }
      setTrailValueType(2);
      setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
      if (val !== "Comb Prem") {
        setPerLot(false);
      }
      setFormData(updated);
    }
  };

  const clickActionSave = () => {
    // Validate stop loss
    const stopLossValue = formData?.strategyRms?.stopLossInput?.value;
    if (stopLossValue !== null && stopLossValue !== undefined && stopLossValue !== '' && stopLossValue !== 0) {
      if (!isValidsltp("sl", stopLossValue)) {
        return;
      }
    }

    // Validate target
    const targetValue = formData?.strategyRms?.takeProfitInput?.value;
    if (targetValue !== null && targetValue !== undefined && targetValue !== '' && targetValue !== 0) {
      if (!isValidsltp("tp", targetValue)) {
        return;
      }
    }

    // Validate trail SL
    const trailRef = formData?.strategyRms?.trailSL?.reference;
    const trailStep = formData?.strategyRms?.trailSL?.step;
    if (!validateTrailSL(trailRef, trailStep)) {
      return;
    }

    // Validate profit lock
    if (!validateProfitLock(formData?.strategyRms?.profitLockAt, formData?.strategyRms?.profitLockSl)) {
      return;
    }

    // Validate profit lock trail
    const profitTrailRef = formData?.strategyRms?.profitLockTrail?.reference;
    const profitTrailStep = formData?.strategyRms?.profitLockTrail?.step;
    if (!validateProfitLockTrail(profitTrailRef, profitTrailStep)) {
      return;
    }

    const basisIsPrice = (formData?.sltpType === "Comb Prem");
    const norm = (n) => {
      if (n === 0) return 0;
      if (n === undefined || n === null || n === "") return null;
      const p = parseFloat(n);
      return Number.isNaN(p) ? null : p;
    };
    const sr = formData.strategyRms || {};
    const updatedStrategyRms = {
      basis: basisIsPrice ? 1 : 0,
      takeProfitInput: sr.takeProfitInput && norm(sr.takeProfitInput.value) !== null ? {
        valueType: tpValueType ?? 2,
        value: norm(sr.takeProfitInput.value)
      } : null,
      stopLossInput: sr.stopLossInput && norm(sr.stopLossInput.value) !== null ? {
        valueType: slValueType ?? 2,
        value: norm(sr.stopLossInput.value)
      } : null,
      trailSL: sr.trailSL && (norm(sr.trailSL.reference) !== null || norm(sr.trailSL.step) !== null) ? {
        valueType: (trailValueType === 1 ? 1 : 2),
        reference: norm(sr.trailSL.reference),
        step: norm(sr.trailSL.step)
      } : null,
      profitLockAt: norm(sr.profitLockAt),
      profitLockSl: norm(sr.profitLockSl),
      profitLockTrail: sr.profitLockTrail && (norm(sr.profitLockTrail.reference) !== null || norm(sr.profitLockTrail.step) !== null) ? {
        valueType: 2,
        reference: norm(sr.profitLockTrail.reference),
        step: norm(sr.profitLockTrail.step)
      } : null,
      perLot: formData.sltpType === "Comb Prem" ? perLot : false
    };
    const value = { ...formData, strategyRms: updatedStrategyRms };
    onSave({ formData: value });
  };

  const clearfield = (name) => {
    const updated = { ...formData, strategyRms: { ...(formData.strategyRms || {}) } };
    if (name === "stopLossAbsolute") {
      updated.strategyRms.stopLossInput = null;
      updated.strategyRms.trailSL = null;
      setErrorMessages((prev) => ({ ...prev, stopLoss: "" }));
    } else if (name === "takeProfitAbsolute") {
      updated.strategyRms.takeProfitInput = null;
      setErrorMessages((prev) => ({ ...prev, target: "" }));
    } else if (name === "trailReferenceAbsolute") {
      if (updated.strategyRms.trailSL) {
        updated.strategyRms.trailSL = {
          ...updated.strategyRms.trailSL,
          reference: null
        };
        if (updated.strategyRms.trailSL.step === null) {
          updated.strategyRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          // Validate after clearing reference - one field is null, other has value
          validateTrailSL(null, updated.strategyRms.trailSL.step);
        }
      }
    } else if (name === "trailStepAbsolute") {
      if (updated.strategyRms.trailSL) {
        updated.strategyRms.trailSL = {
          ...updated.strategyRms.trailSL,
          step: null
        };
        if (updated.strategyRms.trailSL.reference === null) {
          updated.strategyRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          // Validate after clearing step - one field is null, other has value
          validateTrailSL(updated.strategyRms.trailSL.reference, null);
        }
      }
    } else if (name === "profitLockAt") {
      updated.strategyRms.profitLockAt = null;
      updated.strategyRms.profitLockTrail = null;
    } else if (name === "profitLockSl") {
      updated.strategyRms.profitLockSl = null;
      updated.strategyRms.profitLockTrail = null;
    } else if (name === "profitLockTrailRef") {
      const otherValue = formData?.strategyRms?.profitLockTrail?.step;
      if (updated.strategyRms.profitLockTrail) {
        updated.strategyRms.profitLockTrail = {
          ...updated.strategyRms.profitLockTrail,
          reference: null
        };
        if (updated.strategyRms.profitLockTrail.step === null) {
          updated.strategyRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          // Validate after clearing reference - one field is null, other has value
          validateProfitLockTrail(null, updated.strategyRms.profitLockTrail.step);
        }
      } else if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        // If structure doesn't exist but other field has value, validate
        validateProfitLockTrail(null, otherValue);
      }
    } else if (name === "profitLockTrailStep") {
      const otherValue = formData?.strategyRms?.profitLockTrail?.reference;
      if (updated.strategyRms.profitLockTrail) {
        updated.strategyRms.profitLockTrail = {
          ...updated.strategyRms.profitLockTrail,
          step: null
        };
        if (updated.strategyRms.profitLockTrail.reference === null) {
          updated.strategyRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          // Validate after clearing step - one field is null, other has value
          validateProfitLockTrail(updated.strategyRms.profitLockTrail.reference, null);
        }
      } else if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        // If structure doesn't exist but other field has value, validate
        validateProfitLockTrail(otherValue, null);
      }
    }


    setFormData(updated);
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} size="sm" centered className="fixed-modal-sltp ">
      <Modal.Header style={{ position: 'relative' }}>
        <Modal.Title style={{ minWidth: 0 }} className="modal-title-size" id="contained-modal-title-vcenter">
          Default Strategy Risk Settings
        </Modal.Title>
        <button type="button" className="modal-close-button" onClick={() => setShow(false)} aria-label="Close">×</button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-sltp">
          <div style={{ justifyContent: "space-between" }} className="sltp-div modal-input ">
            <Form.Group className="form-group">
              <label className="form-label">SL/TP Basis</label>
              <Form.Select
                aria-label="Default select example"
                name="sltpType"
                value={formData.sltpType}
                onChange={(e) => handleFieldChange(e.target.value, "sltpType", popUpData?.headersValue)}
              >
                {defSLTP.map((data, index) => (
                  <option value={data} key={index}>{data}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {formData.sltpType === 'Comb Prem' && (
              <Form.Group className="form-group-checkbox mt-3">
                <Form.Check
                  type="checkbox"
                  id="per-lot-checkbox"
                  label="1 Lot RMS"
                  checked={perLot}
                  onChange={handlePerLotChange}
                />
              </Form.Group>
            )}
          </div>

          {showScaleWarning && (
            <div className="info-note mb-2" style={{ fontSize: '10px', padding: '8px 10px' }}>
              <Info size={12} style={{ color: '#faad14', marginRight: '4px' }} />
              <span>Scale changed! Please re-verify Absolute SL/TP (Values are now Per Lot).</span>
            </div>
          )}
          {(
            <div className={"sltp-div"}>
              <div className="sltp-col-left">
                <Form.Label style={{ fontWeight: '600' }} className="sltp-label-above">Stop Loss</Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.stopLoss ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "stopLossAbsolute", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.stopLossInput?.value ?? null}
                      name={"stopLossAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(slValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(slValueType)}</div>
                  )}
                  <X size={16} onClick={() => clearfield("stopLossAbsolute")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                </div>
                {errorMessages.stopLoss && (<div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.stopLoss}</div>)}
              </div>
              <div className="sltp-col-right">
                <Form.Label style={{ fontWeight: '600' }} className="sltp-label-above">Target</Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.target ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "takeProfitAbsolute", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.takeProfitInput?.value ?? null}
                      name={"takeProfitAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(tpValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(tpValueType)}</div>
                  )}
                  <X size={16} onClick={() => clearfield("takeProfitAbsolute")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                </div>
                {errorMessages.target && (<div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.target}</div>)}
              </div>
            </div>
          )}

          {(
            <div className="sltp-div-profile">
              <p className="sltp-trail-header">Stoploss Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">For every X move</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "trailReferenceAbsolute", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.trailSL?.reference ?? null}
                      name={"trailReferenceAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData?.strategyRms?.stopLossInput || formData?.strategyRms?.stopLossInput?.value === null}
                      formatINR={true}
                    />
                    {formData?.sltpType === "PNL" ? (
                      <div className="pts-class">{renderPNLLabel(0)}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X size={16} onClick={() => clearfield("trailReferenceAbsolute")} className={`sltp-delete-icon ${(!formData?.strategyRms?.stopLossInput || formData?.strategyRms?.stopLossInput?.value === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Trail SL by Y</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "trailStepAbsolute", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.trailSL?.step ?? null}
                      name={"trailStepAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData?.strategyRms?.stopLossInput || formData?.strategyRms?.stopLossInput?.value === null}
                      formatINR={true}
                    />
                    {formData?.sltpType === "PNL" ? (
                      <div className="pts-class">{renderPNLLabel(0)}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X size={16} onClick={() => clearfield("trailStepAbsolute")} className={`sltp-delete-icon ${(!formData?.strategyRms?.stopLossInput || formData?.strategyRms?.stopLossInput?.value === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.trailSL && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.trailSL}
                </div>
              )}
              <p className="sltp-trail-header">Profit Locking</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockAt", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.profitLockAt || null}
                      name={"profitLockAt"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockAt")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Lock minimum profit at</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockSl", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.profitLockSl || null}
                      name={"profitLockSl"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockSl")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.profitLock && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLock}
                </div>
              )}

              <p className="sltp-trail-header">Profit Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailRef", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.profitLockTrail?.reference || null}
                      name={"profitLockTrailRef"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={formData?.strategyRms?.profitLockAt === null || formData?.strategyRms?.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockTrailRef")} className={`sltp-delete-icon ${(formData?.strategyRms?.profitLockAt === null || formData?.strategyRms?.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailStep", popUpData.headersValue)}
                      filterValue={formData?.strategyRms?.profitLockTrail?.step || null}
                      name={"profitLockTrailStep"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={formData?.strategyRms?.profitLockAt === null || formData?.strategyRms?.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockTrailStep")} className={`sltp-delete-icon ${(formData?.strategyRms?.profitLockAt === null || formData?.strategyRms?.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.profitLockTrail && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLockTrail}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Summary Section */}
        <div className="sltp-summary-section">
          <div className="sltp-summary-title">
            Summary
          </div>
          <div className="sltp-summary-body">
            {(() => {
              const summaryLines = [];
              const basisLabel = formData?.sltpType === "PNL" ? "PnL" : "combined premium";

              // Stop Loss
              const slValue = formData?.strategyRms?.stopLossInput?.value;
              if (slValue !== null && slValue !== undefined && slValue !== '' && slValue !== 0) {
                const parsedSl = parseFloat(slValue);
                if (formData?.sltpType === "PNL") {
                  if (slValueType === 0) {
                    summaryLines.push(`If ${basisLabel} falls to ₹${Math.abs(parsedSl).toFixed(2)}, the strategy will exit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} falls by ${Math.abs(parsedSl).toFixed(2)} pts, the strategy will exit.`);
                  }
                } else {
                  if (slValueType === 1) {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedSl.toFixed(2)}%, the strategy will exit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedSl.toFixed(2)} pts, the strategy will exit.`);
                  }
                }
              }

              // Target
              const tpValue = formData?.strategyRms?.takeProfitInput?.value;
              if (tpValue !== null && tpValue !== undefined && tpValue !== '' && tpValue !== 0) {
                const parsedTp = parseFloat(tpValue);
                if (formData?.sltpType === "PNL") {
                  if (tpValueType === 0) {
                    summaryLines.push(`If ${basisLabel} touches ₹${Math.abs(parsedTp).toFixed(2)}, the strategy will exit with profit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves up by ${Math.abs(parsedTp).toFixed(2)} pts, the strategy will exit with profit.`);
                  }
                } else {
                  if (tpValueType === 1) {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedTp.toFixed(2)}%, the strategy will exit with profit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedTp.toFixed(2)} pts, the strategy will exit with profit.`);
                  }
                }
              }

              // Stoploss Trailing
              const trailRef = formData?.strategyRms?.trailSL?.reference;
              const trailStep = formData?.strategyRms?.trailSL?.step;
              if ((trailRef !== null && trailRef !== undefined && trailRef !== '') &&
                (trailStep !== null && trailStep !== undefined && trailStep !== '')) {
                const parsedRef = parseFloat(trailRef);
                const parsedStep = parseFloat(trailStep);
                if (formData?.sltpType === "PNL") {
                  summaryLines.push(`For every ₹${parsedRef.toFixed(2)} increase in profit, SL will move up by ₹${parsedStep.toFixed(2)}.`);
                } else {
                  if (trailValueType === 1) {
                    summaryLines.push(`For every ${parsedRef.toFixed(2)}% increase in profit, SL will move up by ${parsedStep.toFixed(2)}%.`);
                  } else {
                    summaryLines.push(`For every ${parsedRef.toFixed(2)} pts increase in profit, SL will move up by ${parsedStep.toFixed(2)} pts.`);
                  }
                }
              }

              // Profit Locking
              const profitAt = formData?.strategyRms?.profitLockAt;
              const profitSl = formData?.strategyRms?.profitLockSl;
              if ((profitAt !== null && profitAt !== undefined && profitAt !== '') &&
                (profitSl !== null && profitSl !== undefined && profitSl !== '')) {
                const parsedAt = parseFloat(profitAt);
                const parsedSl = parseFloat(profitSl);
                summaryLines.push(`When profit reaches ₹${parsedAt.toFixed(2)}, at least ₹${parsedSl.toFixed(2)} will be protected.`);
              }

              // Profit Trailing
              const profitTrailRef = formData?.strategyRms?.profitLockTrail?.reference;
              const profitTrailStep = formData?.strategyRms?.profitLockTrail?.step;
              if ((profitTrailRef !== null && profitTrailRef !== undefined && profitTrailRef !== '') &&
                (profitTrailStep !== null && profitTrailStep !== undefined && profitTrailStep !== '')) {
                const parsedRef = parseFloat(profitTrailRef);
                const parsedStep = parseFloat(profitTrailStep);
                summaryLines.push(`Each time profit increases by ₹${parsedRef.toFixed(2)}, locked profit will rise by ₹${parsedStep.toFixed(2)}.`);
              }

              if (summaryLines.length === 0) {
                return <div style={{ color: '#6b7785', fontStyle: 'italic' }}>No risk settings configured</div>;
              }

              return summaryLines.map((line, index) => (
                <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>
                  {line}
                </div>
              ));
            })()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleReset} variant="outline-primary">Reset</Button>
        <Button disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail} onClick={() => { clickActionSave(); setShow(false); }}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StrategyRiskModal;